import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Popover,
  Snackbar,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import {
  activityList,
  addProgressToActivities,
  DownloadExcelFile,
  getColumnsList,
  roundDown
} from "../../../shared/Helper";
import reportsAtoms, {
  fetchSummaryList,
  getActivity,
  getConstructionProgressList
} from "../../../atoms/reports/ReportsAtom";
import settingsAtom from "../../../atoms/settings/SettingsAtom";
import DownloadIcon from '@mui/icons-material/Download';

const Summary = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [checkedList, setCheckedList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [colValue, setColValue] = useState([]);
  const [, setConstruction] = useAtom(getActivity);
  const [, setProgress] = useAtom(getConstructionProgressList);
  const [, setData] = useAtom(fetchSummaryList);
  const [data] = useAtom(reportsAtoms);
  const [settingsData] = useAtom(settingsAtom);
  const [list, setList] = useState([]);
  const { constructionList, progressList } = data || {};
  const { projectList } = settingsData;
  const currentProject = projectList.find(o => o.id === parseInt(localStorage.getItem("project_id")));
  const { construction } = constructionList || {};
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  const fetchData = async () => {
    setLoader(true);
    try {
      await setData({ activity_type: "construction", is_summary: true });
      await setConstruction("");
      await setProgress("");
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (construction) {
      const temp = activityList(construction);
      const updatedList = addProgressToActivities(temp, progressList || []);
      setList(updatedList);

      const result = convertToNewFormat(updatedList);
      const defaultCheckedList = result
        .map((item) => item?.key && !item.hidden ? item.key : null)
        .filter(Boolean);
      setCheckedList(defaultCheckedList);

      const updatedColumns = result.map((item) => ({
        ...item,
        hidden: !defaultCheckedList.includes(item.key)
      })).filter(Boolean);

      updatedColumns.unshift({
        key: 0,
        value: "blocks",
        hidden: false,
        parent: true
      });

      setColValue(updatedColumns);
    }
  }, [construction, progressList]);

  useEffect(() => {
    if (data?.summaryData) {
      const value = Object.values(data.summaryData)?.map(blockActivities => {
        const block = blockActivities[0];
        const result = {
          id: block?.block_id, // <-- Ensure `id` exists
          block_id: block?.block_id,
          block_name: block?.block_name
        };

        blockActivities.forEach((activity) => {
          const key = activity.activity_name.toLowerCase().replace(/\s+/g, "_");
          result[key] = {
            progress: activity.progress,
            deployed_count: activity.deployed_count,
            total_count: activity.total_count,
            activity_id: activity.activity_id
          };
        });

        return result;
      }).filter(Boolean); // <-- Filter out null/undefined

      setTableData(value);
    }
  }, [data]);

  useEffect(() => {
    const updatedColumns = convertToNewFormat(list)?.map((item) => ({
      ...item,
      hidden: !checkedList.includes(item.key)
    }));
    updatedColumns.unshift({
      key: 0,
      value: "blocks",
      hidden: false,
      parent: true
    });
    setColValue(updatedColumns);
  }, [checkedList, list]);

  const onChange = (key) => {
    setCheckedList(prevList =>
      prevList.includes(key)
        ? prevList.filter((item) => item !== key)
        : [...prevList, key]
    );
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const convertToNewFormat = (arr) => {
  const result = [];

  arr.forEach((item) => {
    result.push({
      value: item?.label?.toLowerCase().replace(/\s+/g, "_"),
      key: item.id,
      children: ["progress"], // Remove "counts" from parent activities
      hidden: false,
      parent: true
    });

    item.children?.forEach((child) => {
      result.push({
        value: child?.label?.toLowerCase().replace(/\s+/g, "_"),
        key: child.id,
        children: ["progress", "counts"], // Keep both "progress" and "counts" for children
        hidden: false,
        parent: false
      });
    });
  });

  return result;
};
  const prepareColumnsAndGroups = (colValue) => {
    const columnGroupingModel = [];
    const processedGroupIds = new Set();
    const uniqueFieldsMap = new Map();

    const visibleColumns = colValue
      .filter((col) => !col.hidden)
      .flatMap((col) => {
        const getWidth = (value) => {
          if (value === 'part_name') return 200;
          if (value === 'part_description') return 360;
          return 120;
        };

        if (col.children?.length) {
          return col.children.map((child) => {
            const fieldName = `${col.value}.${child}`;

            if (uniqueFieldsMap.has(fieldName)) return null;
            uniqueFieldsMap.set(fieldName, true);
            const columnWidth = getWidth(fieldName);
            return {
              field: fieldName,
              headerName: child.charAt(0).toUpperCase() + child.slice(1),
              headerClassName: col.parent ? `header-parent-${col.value}` : `header-child`,
              sortable: false,
              filterable: false,
              width: columnWidth,
              minWidth: columnWidth,
              flex: 1,
              renderCell: (params) => {
                const value = params.row;
                if (child === 'progress') {
                  return <div>{roundDown(value[col.value]?.progress) || 0}%</div>;
                } else if (child === 'counts') {
                  return <div>{value[col.value]?.deployed_count || 0} of {value[col.value]?.total_count || 0}</div>;
                } else {
                  return <div>{value[col.value]?.[child] || ''}</div>;
                }
              }
            };
          }).filter(Boolean);
        } else {
          const columnWidth = getWidth(col.value);
          if (uniqueFieldsMap.has(col.value)) return null;
          uniqueFieldsMap.set(col.value, true);

          return {
            field: col.value,
            headerName: col.value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
            sortable: false,
            filterable: true,
            width: columnWidth,
            minWidth: columnWidth,
            pinned: col.value === "blocks" ? 'left' : undefined,
            headerClassName: col.parent ? `header-parent` : `header-child`,
            renderCell: (params) => {
              if (col.value === 'blocks') {
                return <div style={{ fontWeight: 600 }}>{params.row.block_name}</div>;
              }
              return <div>{params.value}</div>;
            }
          };
        }
      }).filter(Boolean);

    visibleColumns.forEach(col => {
      if (col.field && col.field.includes('.')) {
        const groupId = col.field.split('.')[0];
        if (!processedGroupIds.has(groupId)) {
          processedGroupIds.add(groupId);

          const groupColumns = visibleColumns.filter(c =>
            c.field && c.field.startsWith(`${groupId}.`)
          );

          if (groupColumns.length > 0) {
            columnGroupingModel.push({
              groupId,
              headerName: groupId.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
              headerClassName: col?.headerClassName === "header-child" ? `header-group-child` : 'header-group-parent',
              children: groupColumns.map(c => ({ field: c.field })),
              headerAlign: 'center',
            });
          }
        }
      }
    });

    return { visibleColumns, columnGroupingModel };
  };

  const { visibleColumns, columnGroupingModel } = prepareColumnsAndGroups(colValue);

  const handleDownload = async (headers, data, currentProject, setSnackbar, title) => {
    setLoading(true);
    try {
      await DownloadExcelFile(headers, data, currentProject, setSnackbar, title);
      setSnackbar({ open: true, message: 'Downloaded construction progress summary', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: 'Error downloading Excel.' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box px={4} py={2} mt={1.5} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <Box pb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontWeight={600} fontSize="1.25rem">Summary</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={() => handleDownload(colValue, tableData, currentProject, setSnackbar, "CP Summary")}
            disabled={loading}
            startIcon={<DownloadIcon />}
          >
            {loading ? 'Downloading...' : 'Download'}
          </Button>
          <Button variant="outlined" onClick={handleClick}>Columns</Button>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ style: { maxHeight: 380, width: 220 } }}
          >
            <Box p={1}>
              <Typography variant="span" fontSize={16} fontWeight={600}>Columns List</Typography>
              {getColumnsList(convertToNewFormat(list), checkedList, onChange)}
            </Box>
          </Popover>
        </Box>
      </Box>

      {loader ? (
        <Box display="flex" height="73vh" alignItems="center" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <div style={{ height: "73vh", width: '100%', backgroundColor: '#ffffff' }}>
          <DataGrid
            rows={tableData?.filter(row => row && row.id != null)}
            getRowId={(row) => row.id}
            columns={visibleColumns}
            experimentalFeatures={{ columnGrouping: true }}
            columnGroupingModel={columnGroupingModel}
            hideFooterPagination
            hideFooter
            disableColumnMenu
            disableSelectionOnClick
            pagination={false}
            sx={{
              '& .MuiDataGrid-columnSeparator': {
                color: '#fff',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                width: '100%',
                color: '#fff',
              },
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: '#f16253',
                color: '#fff',
              },
              '& .MuiDataGrid-columnGroupHeader': {
                backgroundColor: '#f16253',
                color: '#fff',
                fontWeight: 600,
                textAlign: 'center',
              },
              '& .MuiDataGrid-columnHeadersInner': {
                backgroundColor: '#f16253',
              },
              '& .MuiDataGrid-columnHeader--emptyGroup': {
                backgroundColor: '#f16253',
              },
              '& .header-parent, .header-group-parent, .header-child, .header-group-child': {
                backgroundColor: '#f16253',
                color: '#fff',
              },
              // Additional selectors to ensure all header elements get the styling
              '& [role="columnheader"]': {
                backgroundColor: '#f16253',
                color: '#fff',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f16253',
              },
            }}
          />
        </div>
      )}
    </Box>
  );
};

export default Summary;